import React, { createContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { THEMES } from 'src/constants';
import * as LanternApi from 'src/utilities/LanternApiClient';
import { getUserSettings } from 'src/utilities/AppCaches';
import { enqueueSnackbar } from 'notistack';

const defaultSettings = {
  direction: 'ltr',
  responsiveFontSizes: true,
  theme: THEMES.DARK,
};

export const restoreSettings = async () => {
  // Check if user is authenticated before making API calls
  const authToken = localStorage.getItem('auth');

  // Also check if MSAL has any accounts (more reliable than localStorage)
  let hasAccounts = false;
  try {
    if (typeof window !== 'undefined' && window.__msalInstance) {
      await window.__msalInstance.initialize();
      const accounts = window.__msalInstance.getAllAccounts();
      hasAccounts = accounts.length > 0;
    }
  } catch (error) {
    console.warn('Failed to check MSAL accounts:', error);
  }

  if (!authToken || !hasAccounts) {
    console.log('No valid authentication available, using default settings');
    return defaultSettings;
  }

  try {
    // Use cached user settings
    const settings = await getUserSettings();
    return settings;
  } catch (error) {
    // If user settings don't exist, create them with defaults
    if (error.message.includes('Failed to fetch user settings')) {
      console.log('User settings not found, creating with defaults');
      try {
        var createResponse = await LanternApi.initializeUserSettings();

        if (createResponse.success) {
          return createResponse.body;
        } else {
          console.warn('Failed to create user settings, using defaults');
          return defaultSettings;
        }
      } catch (createError) {
        console.warn('Failed to create user settings, using defaults');
        return defaultSettings;
      }
    } else {
      console.warn('Failed to restore settings from API:', error);
      return defaultSettings;
    }
  }
};

export const storeSettings = async (updatedSettings) => {
  // Check if user is authenticated before making API calls
  const authToken = localStorage.getItem('auth');

  // Also check if MSAL has any accounts (more reliable than localStorage)
  let hasAccounts = false;
  try {
    if (typeof window !== 'undefined' && window.__msalInstance) {
      await window.__msalInstance.initialize();
      const accounts = window.__msalInstance.getAllAccounts();
      hasAccounts = accounts.length > 0;
    }
  } catch (error) {
    console.warn('Failed to check MSAL accounts:', error);
  }

  if (!authToken || !hasAccounts) {
    console.log('No valid authentication available, skipping settings save');
    return;
  }

  try {
    var response = await LanternApi.updateUserSettings(updatedSettings);

    if (response.success) {
      enqueueSnackbar('Settings saved.', {
        variant: 'success',
      });
    } else {
      enqueueSnackbar('Failed to save settings.', {
        variant: 'error',
      });
    }
  } catch (error) {
    console.warn('Failed to save settings:', error);
    enqueueSnackbar('Failed to save settings.', {
      variant: 'error',
    });
  }
};

const SettingsContext = createContext({
  settings: defaultSettings,
  saveSettings: () => {},
});

export const SettingsProvider = ({ settings, children }) => {
  const [currentSettings, setCurrentSettings] = useState(settings || defaultSettings);

  const handleSaveSettings = (newSettings = {}) => {
    const mergedSettings = _.merge({}, currentSettings, newSettings);

    setCurrentSettings(mergedSettings);
    storeSettings(mergedSettings);
  };

  useEffect(() => {
    const initializeSettings = async () => {
      try {
        const restoredSettings = await restoreSettings();

        if (restoredSettings) {
          setCurrentSettings(restoredSettings);
        }
      } catch (error) {
        console.warn('Failed to restore settings, using defaults:', error);
      }
    };

    // Add a small delay to allow auth context to initialize first
    const timeoutId = setTimeout(() => {
      initializeSettings();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    document.dir = currentSettings.direction;
  }, [currentSettings]);

  return (
    <SettingsContext.Provider
      value={{
        settings: currentSettings,
        saveSettings: handleSaveSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;

export default SettingsContext;
